// app_core/src/map/map-transitions.js

import { MAP_ANIMATION_SETTINGS, ZOOM_BEHAVIOR_SETTINGS, LOGGING } from '/settings.js';
import { clearAllSpots } from './map-visibility.js';
import { displaySpots, getMap, getMarkers } from './map.js';
import { calculateOptimalZoom } from './map-movements.js';
import { clearAllUI } from '../utils/clearAll.js';

/**
 * Executes a fade-out/fade-in transition on the map.
 * @param {L.Map} map - The Leaflet map instance.
 * @param {Function} action - The async function to execute while the map is faded out.
 * @param {Object} options - Options for the transition.
 * @param {Array} options.popupsToFade - Array of popup instances to fade out.
 * @param {Array} options.targetPanes - Optional specific Leaflet panes to fade (e.g., ['markerPane', 'popupPane']).
 * @param {Function} [onDataUpdate] - Optional callback to update other data after delay.
 */
async function fadeTransition(map, action, options = {}, onDataUpdate = null) {
    const { popupsToFade = [], targetPanes = null } = options;

    // Select panes to fade: either specific ones or everything in the container
    let elementsToFade;
    if (targetPanes && targetPanes.length > 0) {
        // Map pane names to elements
        elementsToFade = targetPanes
            .map(paneName => map.getPane(paneName))
            .filter(Boolean);
    } else {
        // Default: Fade everything inside the map container
        elementsToFade = Array.from(map.getContainer().querySelectorAll('.leaflet-pane, .leaflet-control-container'));
    }

    const duration = MAP_ANIMATION_SETTINGS.FADE_DURATION_MS || 300;

    // Fade out target elements simultaneously
    if (LOGGING.ENABLED) console.log(`[Map Transition] Fading out ${targetPanes ? targetPanes.join(', ') : 'all'} for ${duration}ms...`);

    elementsToFade.forEach(el => {
        el.style.transition = `opacity ${duration}ms ease-in-out`;
        el.style.opacity = 0;
    });

    // Fade out popups using their fadeOut method
    const popupFadePromises = popupsToFade.map(popup => {
        if (popup && typeof popup.fadeOut === 'function') {
            return popup.fadeOut();
        }
        return Promise.resolve();
    });

    // Wait for map fade duration
    await Promise.all([
        new Promise(resolve => setTimeout(resolve, duration)),
        ...popupFadePromises
    ]);

    // Perform the MAP update action immediately (e.g., clear and add new spots)
    await action();

    // Fade in target elements immediately
    if (LOGGING.ENABLED) console.log('[Map Transition] Fading in...');
    elementsToFade.forEach(el => {
        el.style.opacity = 1;
    });

    // Wait for fade-in to complete
    await new Promise(resolve => setTimeout(resolve, duration));

    // Update Card Data after map transition completes
    if (onDataUpdate) {
        await onDataUpdate();
    }

    // Clean up transitions
    elementsToFade.forEach(el => {
        el.style.transition = '';
    });
    if (LOGGING.ENABLED) console.log('[Map Transition] Fade complete.');
}

/**
 * Executes a pan transition on the map with card updates.
 * @param {L.Map} map - The Leaflet map instance.
 * @param {Function} action - The async function to execute (displays spots and pans).
 * @param {Function} [onDataUpdate] - Optional callback to update other data (like cards) after pan completes.
 */
async function panTransition(map, action, onDataUpdate = null) {
    const panDuration = MAP_ANIMATION_SETTINGS.PAN_DURATION_SECONDS || 1;

    if (LOGGING.ENABLED) console.log(`[Map Transition] Starting pan animation...`);

    // Perform the MAP update action (displays spots and starts panning)
    await action();

    // Wait for pan animation to complete by listening to moveend event
    // This is more reliable than using a fixed duration
    await new Promise((resolve) => {
        const onMoveEnd = () => {
            map.off('moveend', onMoveEnd);
            resolve();
        };
        map.on('moveend', onMoveEnd);

        // Fallback timeout in case moveend doesn't fire
        setTimeout(() => {
            map.off('moveend', onMoveEnd);
            resolve();
        }, (panDuration * 1000) + 500);
    });

    // Update Card Data after pan completes
    if (onDataUpdate) {
        await onDataUpdate();
    }

    if (LOGGING.ENABLED) console.log('[Map Transition] Pan complete.');
}

/**
 * Transitions the map to a specific area (lat, lng, zoom) using fade effect.
 * @param {number} lat 
 * @param {number} lng 
 * @param {number} zoom 
 */
export async function transitionToArea(lat, lng, zoom) {
    const map = getMap();
    if (!map) return;

    const updateAction = () => {
        clearAllSpots(); // Ensure spots are cleared
        map.setView([lat, lng], zoom, { animate: false });
    };

    if (MAP_ANIMATION_SETTINGS.TYPE === 'fade') {
        await fadeTransition(map, updateAction);
    } else {
        // Fallback to simple setView if fade disabled
        updateAction();
    }
}

/**
 * Centralized function to update the map view with new spots,
 * applying either a 'pan' or 'fade' animation based on settings.
 * @param {object} options
 * @param {Array} options.spotsToDisplay - The array of spot objects to display.
 * @param {Array} [options.searchedTags=[]] - Tags that were searched for, to highlight them.
 * @param {boolean} [options.shouldAnimate=true] - Whether to apply an animation (pan or fade).
 * @param {Array} [options.popupsToFade=[]] - Array of popup instances to fade out with the map.
 */
export async function updateMapView(options) {
    const { spotsToDisplay, searchedTags = [], shouldAnimate = true, popupsToFade = [], keepFavoritesActive = false } = options;
    const map = getMap();

    // This is the core action: clear old spots, add new ones, and set the view WITHOUT animation.
    const updateAction = async () => {
        clearAllSpots();
        // Display spots without panning, as we control the view change manually.
        displaySpots(spotsToDisplay, searchedTags, false);

        // Calculate optimal zoom and position WITHOUT animation
        const markers = getMarkers();
        if (markers.length > 0) {
            const group = new L.featureGroup(markers);
            const bounds = group.getBounds();
            const optimalZoom = calculateOptimalZoom(bounds, map, markers.length);

            // Set the view WITHOUT animation for fade transitions
            map.setView(bounds.getCenter(), optimalZoom, { animate: false });
            map.fitBounds(bounds, {
                padding: ZOOM_BEHAVIOR_SETTINGS.BOUNDS_PADDING,
                animate: false,
                maxZoom: optimalZoom
            });
        }
    };

    // Decide which transition to use based on settings.
    if (MAP_ANIMATION_SETTINGS.TYPE === 'fade' && shouldAnimate) {
        // Full map crossfade transition: fades all panes (base map, markers, popups)
        const targetPanes = null;

        await fadeTransition(map, updateAction, {
            popupsToFade,
            targetPanes
        }, options.onDataUpdate);
    } else if (shouldAnimate) {
        // Pan animation: close popups, then pan with card updates
        clearAllUI({ keepMenu: true, keepCards: true, keepFavorites: keepFavoritesActive });

        // Create pan action that displays spots and pans
        const panAction = async () => {
            clearAllSpots();
            displaySpots(spotsToDisplay, searchedTags, true); // true = shouldPan
        };

        await panTransition(map, panAction, options.onDataUpdate);
    } else {
        // No animation: just update immediately
        clearAllUI({ keepMenu: true, keepCards: true, keepFavorites: keepFavoritesActive });
        clearAllSpots();
        displaySpots(spotsToDisplay, searchedTags, false);
        // Still update cards if callback provided
        if (options.onDataUpdate) {
            await options.onDataUpdate();
        }
    }
}
